import { CellWriteCoalescer } from "../stores/cell-write-coalescer.ts";
/**
 * CellWrites — the ONE cell write ledger of a shared cell-store scope: every optimistic cell
 * write the rows side paints before the server answers, on the one ledger
 * engine (`Ledger`). Sheets sharing a canonical store hold the same instance;
 * the sheet's `CellWriteCoalescer` is only the debounce policy of the cell
 * transport.
 *
 * Two lanes share the instance, each keyed by its cell:
 *
 * - `dated` — a date-keyed cell (`CellWriteCoalescer.key(rowKey,
 *   "<date>:<cellId>")`), a write of its value (`null`: cleared);
 * - `timepoint` — a timepoint (custom) cell (`CellWriteCoalescer.key(rowKey,
 *   "tp:<cellId>")`), a write of the whole cell (`null`: removed).
 *
 * The engine's settle rules are the cell's:
 *
 * - issue order: `projection = confirmed ⊕ write₁ ⊕ … ⊕ writeₙ` — the newest
 *   write still waiting is what the cell shows;
 * - an accepted write becomes the confirmed value once every older write of
 *   the cell settled, and an older write's later verdict changes nothing on
 *   screen (a newer write stands);
 * - a refusal takes back only its own write: when it was the one shown, the
 *   cell shows the newest surviving write, else the confirmed value.
 *
 * A cell write's body is its own value: a later body of the same cell never
 * carries an older write's change, so the engine's take-back re-send has
 * nothing to correct, and the transport (`Backend.writeCell` / `clearCell`,
 * the batch POST, through the coalescer) sends outside the engine.
 *
 * It paints nothing and announces nothing itself: `refuse` (and the event
 * settlements) answer what the caller shows, because the caller paints the
 * cell into the sheet's cell store with its own actor.
 *
 * Next to the ledger it keeps the sheet's two other cell-write registries:
 * the durable writes waiting on a debounced persist (`waitDurable` /
 * `takeDurable`), and the event edits waiting on their verdict, settled by
 * event identity across days and lanes (`beginEvent` / `acceptEvent` /
 * `refuseEvent`).
 */

import type { NaiveDate } from "@tento-chrono";
import type { Option } from "../core/option.ts";
import type { CellEntry, CellValue } from "../core/dtype.ts";
import { Actor } from "../tx/invalidation.ts";
import { Ledger } from "./ledger.ts";

/** A timepoint (custom) cell as the ledger holds it. */
type Timepoint = CellEntry.Timepoint;

/** The sheet's cell write ledger (see the module doc). */
export class CellWrites {
  static readonly #scopes = new WeakMap<object, CellWrites>();

  /** The write ledger and debounce transport follow the shared store identity. */
  static forStore(store: object): CellWrites {
    const existing = this.#scopes.get(store);
    if (existing != null) return existing;
    const writes = new CellWrites();
    this.#scopes.set(store, writes);
    return writes;
  }

  /** Trailing persists share the same ownership and issue order as their ledger. */
  readonly coalescer = new CellWriteCoalescer();

  readonly #ledger: Ledger<CellWrites.Types>;
  /** The date-keyed lane: a write of a cell's value (`null`: cleared). */
  readonly dated: CellWrites.Lane<Option<CellValue>>;
  /** The timepoint lane: a write of the whole cell (`null`: removed). */
  readonly timepoint: CellWrites.Lane<Option<Timepoint>>;
  /** The durable writes waiting on a debounced persist, per cell key. */
  readonly #waiters = new Map<string, CellWrites.Waiter[]>();
  /** The event edits waiting on their verdict, per event key, in issue order. */
  readonly #events = new Map<string, CellWrites.EventOp[]>();

  constructor() {
    const strategy: Ledger.Strategy<CellWrites.Types> = {
      empty: () => ({}),
      merge: (_fields, change) => ({ cell: change.box }),
      fieldsOf: () => CELL,
      body: (fields, only) => (only.has("cell") && fields.cell != null ? { cell: fields.cell } : {}),
      adopt: (fields, from, only) => ([...only].includes("cell") ? (from.cell == null ? {} : { cell: from.cell }) : fields),
      overlay: (under, over) => (over.cell == null ? under : { cell: over.cell }),
      takeBack: () => {
        throw new Error("CellWrites: a cell write's body is its own value; nothing is re-sent");
      },
      delta: () => null,
      events: () => Ledger.SILENT,
      deferred: () => {
        throw new Error("CellWrites: a cell write defers no announcement");
      },
      wrap: (err) => (err instanceof Error ? err : new Error(String(err))),
    };
    this.#ledger = new Ledger(strategy, {
      sinks: () => [],
      name: "cell-writes",
      takeBackContext: "cell-writes: take-back",
      describe: (key) => ({ key }),
      // Nothing is sent through the engine (see the module doc): nothing is abandoned.
      live: () => true,
      abandoned: () => {},
    });
    this.dated = new CellWrites.Lane<Option<CellValue>>(this.#ledger, "d|", {
      wrap: (value) => ({ lane: "dated", value }),
      unwrap: (held) => (held.lane === "dated" ? { value: held.value } : null),
      absent: null,
    });
    this.timepoint = new CellWrites.Lane<Option<Timepoint>>(this.#ledger, "t|", {
      wrap: (value) => ({ lane: "timepoint", value }),
      unwrap: (held) => (held.lane === "timepoint" ? { value: held.value } : null),
      absent: null,
    });
  }

  // ── durable waiters ────────────────────────────────────────────────

  /** Wait for the verdict of the debounced persist that carries the cell `key`. */
  waitDurable(key: string, waiter: CellWrites.Waiter): void {
    const list = this.#waiters.get(key);
    if (list === undefined) this.#waiters.set(key, [waiter]);
    else list.push(waiter);
  }

  /** Every write waiting on the cell `key`'s persist (they leave the registry). */
  takeDurable(key: string): readonly CellWrites.Waiter[] {
    const list = this.#waiters.get(key) ?? [];
    this.#waiters.delete(key);
    return list;
  }

  /** Settle every waiter on `verdict` (the persist that carried their cell). */
  static settleDurable(waiters: readonly CellWrites.Waiter[], verdict: CellWrites.Verdict): void {
    for (const w of waiters) w.settle(verdict);
  }

  // ── event edits ────────────────────────────────────────────────────

  /** Enter an edit of `event` (its row + id key), newest last. */
  beginEvent(event: string): CellWrites.EventOp {
    const op: CellWrites.EventOp = { event, writes: [] };
    const list = this.#events.get(event);
    if (list === undefined) this.#events.set(event, [op]);
    else list.push(op);
    return op;
  }

  /**
   * The edit was refused (or never answered). It leaves the registry; when
   * it was the event's newest edit still waiting (or a newer acceptance
   * already retired it), each of its writes is taken back in its cell;
   * otherwise the next newer edit still waiting keeps its optimistic event
   * shown: `op`'s writes leave the cells that edit also wrote, and it adopts
   * the others as "absent". Returns what the caller repaints.
   */
  refuseEvent(op: CellWrites.EventOp): CellWrites.Repaint[] {
    const list = this.#events.get(op.event) ?? [];
    const at = list.indexOf(op);
    if (at >= 0) {
      list.splice(at, 1);
      if (list.length === 0) this.#events.delete(op.event);
    }
    const newer: Option<CellWrites.EventOp> = at >= 0 ? list.at(at) ?? null : null;
    const repaints: CellWrites.Repaint[] = [];
    if (newer == null) {
      for (const entry of op.writes) {
        const repaint = this.#refuseEntry(entry);
        if (repaint != null) repaints.push(repaint);
      }
      return repaints;
    }
    const covered = new Set(newer.writes.map((entry) => entry.write.key));
    for (const entry of op.writes) {
      // The newer edit wrote this cell after `op`: `op`'s write leaves it
      // (the newer write stays shown; what the cell showed before both stays
      // what the newer edit's refusal restores).
      if (covered.has(entry.write.key)) {
        this.#refuseEntry(entry);
        continue;
      }
      const adopted = this.#adoptEntry(entry);
      if (adopted == null) continue;
      newer.writes.push(adopted.entry);
      if (adopted.clears) repaints.push(cleared(adopted.entry));
    }
    return repaints;
  }

  /**
   * The edit was accepted: its writes (and those it adopted) are confirmed in
   * their cells, every older edit of the event leaves the registry, and each
   * of their writes in a cell `op` did not write is retired (`Lane.retire`).
   * Returns the retired writes the screen showed, as clears.
   */
  acceptEvent(op: CellWrites.EventOp): CellWrites.Repaint[] {
    for (const entry of op.writes) {
      if (entry.lane === "dated") this.dated.accept(entry.write);
      else this.timepoint.accept(entry.write);
    }
    const list = this.#events.get(op.event);
    if (list === undefined) return [];
    const at = list.indexOf(op);
    if (at < 0) return [];
    const written = new Set(op.writes.map((entry) => entry.write.key));
    const repaints: CellWrites.Repaint[] = [];
    for (const older of list.slice(0, at)) {
      for (const entry of older.writes) {
        if (written.has(entry.write.key)) continue;
        const shown = entry.lane === "dated" ? this.dated.retire(entry.write) : this.timepoint.retire(entry.write);
        if (shown) repaints.push(cleared(entry));
      }
    }
    list.splice(0, at + 1);
    if (list.length === 0) this.#events.delete(op.event);
    return repaints;
  }

  /** Take `entry`'s write back in its cell: what it shows now, or `null` when unchanged. */
  #refuseEntry(entry: CellWrites.EventEntry): Option<CellWrites.Repaint> {
    if (entry.lane === "dated") {
      const shown = this.dated.refuse(entry.write);
      return shown == null ? null : { lane: "dated", date: entry.date, value: shown.value };
    }
    const shown = this.timepoint.refuse(entry.write);
    return shown == null ? null : { lane: "timepoint", value: shown.value };
  }

  /** `entry`'s write, adopted as "absent" in its cell, or `null` when it no longer waits. */
  #adoptEntry(entry: CellWrites.EventEntry): Option<{ readonly entry: CellWrites.EventEntry; readonly clears: boolean }> {
    if (entry.lane === "dated") {
      const adopted = this.dated.adoptAsAbsent(entry.write);
      return adopted == null ? null : { entry: { lane: "dated", write: adopted.write, date: entry.date }, clears: adopted.clears };
    }
    const adopted = this.timepoint.adoptAsAbsent(entry.write);
    return adopted == null ? null : { entry: { lane: "timepoint", write: adopted.write, date: null }, clears: adopted.clears };
  }
}

/** Types and the lane that go with `CellWrites`. */
export namespace CellWrites {
  /** A value one lane holds, tagged with its lane. */
  export type Value =
    | { readonly lane: "dated"; readonly value: Option<CellValue> }
    | { readonly lane: "timepoint"; readonly value: Option<Timepoint> };

  /**
   * What a write paints, boxed: an event edit turns a waiting write into
   * "absent" in place (`Lane.adoptAsAbsent`), keeping its issue-order place.
   */
  export type Box = { value: Value };

  /** The engine's type bundle for the cell ledger. */
  export type Types = {
    readonly key: string;
    readonly change: { readonly box: Box };
    readonly fields: { readonly cell?: Box };
    readonly field: "cell";
    readonly delta: null;
    readonly answer: unknown;
    readonly error: Error;
  };

  /** One write of a lane's cell (compared by identity). `V`: the lane's value type. */
  export type Write<V> = { readonly key: string; readonly lane: Lane<V> };

  /** A persist's verdict: accepted, or refused (or dropped) with its error. */
  export type Verdict = { readonly accepted: true } | { readonly accepted: false; readonly error: unknown };

  /** A durable write waiting on a debounced persist's verdict. */
  export type Waiter = { readonly settle: (verdict: Verdict) => void };

  /** One ledger write of an event edit: its lane, and the day of a dated one. */
  export type EventEntry =
    | { readonly lane: "dated"; readonly write: Write<Option<CellValue>>; readonly date: NaiveDate.Partial }
    | { readonly lane: "timepoint"; readonly write: Write<Option<Timepoint>>; readonly date: null };

  /** One event edit waiting on its verdict: its writes, and those it adopted. */
  export type EventOp = { readonly event: string; readonly writes: EventEntry[] };

  /** What a settlement leaves shown in one cell of the event (`null`: clear it). */
  export type Repaint =
    | { readonly lane: "dated"; readonly date: NaiveDate.Partial; readonly value: Option<CellValue> }
    | { readonly lane: "timepoint"; readonly value: Option<Timepoint> };

  /** How a lane holds its values in the shared engine. */
  export type Codec<V> = {
    readonly wrap: (value: V) => Value;
    readonly unwrap: (held: Value) => Option<{ readonly value: V }>;
    /** The lane's "absent" value (a cleared or removed cell). */
    readonly absent: V;
  };

  /** One lane of the ledger: its cells' writes, by cell key (see the module doc). */
  export class Lane<V> {
    readonly #ledger: Ledger<Types>;
    readonly #prefix: string;
    readonly #codec: Codec<V>;
    readonly #holds = new WeakMap<Write<V>, () => void>();
    /** Keep the admitted authority alive until this ledger write settles. */
    hold(write: Write<V>, release: () => void): void { this.#holds.set(write, release); }
    #release(write: Write<V>): void { this.#holds.get(write)?.(); this.#holds.delete(write); }
    readonly #engine = new WeakMap<Write<V>, { readonly write: Ledger.Write<Types>; readonly box: Box; settled: boolean }>();

    /** The lane of `ledger` whose keys carry `prefix`, holding its values through `codec`. */
    constructor(ledger: Ledger<Types>, prefix: string, codec: Codec<V>) {
      this.#ledger = ledger;
      this.#prefix = prefix;
      this.#codec = codec;
    }

    /** Record a write about to paint `value` over `prior` (what the cell shows now). */
    begin(key: string, prior: V, value: V): Write<V> {
      const box: Box = { value: this.#codec.wrap(value) };
      const engine = this.#ledger.begin(this.#prefix + key, { box }, Actor.EXTERNAL, {
        correction: Actor.EXTERNAL,
        quiet: false,
        capture: () => ({ cell: { value: this.#codec.wrap(prior) } }),
      });
      const write: Write<V> = Object.freeze({ key, lane: this });
      this.#engine.set(write, { write: engine, box, settled: false });
      return write;
    }

    /**
     * Whether `write` still waits and is its cell's newest write — what the
     * cell shows. `false` once a newer write of the cell was issued (in
     * flight, or accepted first): an answer to `write` must then not repaint
     * the cell.
     */
    standing(write: Write<V>): boolean {
      const state = this.#engine.get(write);
      if (state == null || state.settled) return false;
      return this.#ledger.project(this.#prefix + write.key)?.cell === state.box;
    }

    /** The write was accepted (or can no longer be refused). */
    accept(write: Write<V>): void {
      const state = this.#engine.get(write);
      if (state == null || state.settled) return;
      state.settled = true;
      this.#ledger.accept(state.write);
      this.#release(write);
    }

    /**
     * The write was refused: what the cell must show now (`{ value }`), or
     * `null` when a newer write stands (or the write already settled) and
     * nothing changes on screen.
     */
    refuse(write: Write<V>): Option<{ readonly value: V }> {
      const state = this.#engine.get(write);
      if (state == null || state.settled) return null;
      const shown = this.standing(write);
      state.settled = true;
      const settled = this.#ledger.settle(state.write, { accepted: false, error: new Error("CellWrites: refused"), context: "" });
      this.#release(write);
      if (!shown) return null;
      const box = settled.projection?.cell ?? null;
      return box == null ? { value: this.#codec.absent } : this.#unwrap(box);
    }

    /**
     * A newer accepted edit elsewhere (another cell of the same event) made
     * this cell absent on the server: the write leaves the cell and its
     * confirmed value is absent. Returns `true` when the cell showed this
     * write and shows nothing now, so the caller clears it on screen.
     */
    retire(write: Write<V>): boolean {
      const state = this.#engine.get(write);
      if (state == null || state.settled) return false;
      const shown = this.standing(write);
      const painted = this.#unwrap(state.box).value;
      const key = this.#prefix + write.key;
      this.#ledger.confirm(key, { cell: { value: this.#codec.wrap(this.#codec.absent) } });
      state.settled = true;
      this.#ledger.settle(state.write, { accepted: false, error: new Error("CellWrites: retired by a newer accepted edit"), context: "" });
      this.#release(write);
      return shown && painted != null;
    }

    /**
     * A newer edit of the same event takes over this waiting write's place in
     * its cell as "absent" (its view where it placed no event): the write
     * keeps its issue-order place and paints absent from now on. Returns the
     * write, and `clears` when the cell showed `write`'s value and must
     * clear; `null` when `write` no longer waits.
     */
    adoptAsAbsent(write: Write<V>): Option<{ readonly write: Write<V>; readonly clears: boolean }> {
      const state = this.#engine.get(write);
      if (state == null || state.settled) return null;
      if (this.#unwrap(state.box).value == null) return { write, clears: false };
      const clears = this.standing(write);
      state.box.value = this.#codec.wrap(this.#codec.absent);
      return { write, clears };
    }

    /** Whether a write of the cell `key` still waits on its verdict. */
    inFlight(key: string): boolean {
      return this.#ledger.writing(this.#prefix + key);
    }

    /** What the cell `key` shows while writes of it wait (`null`: none does). */
    shown(key: string): Option<{ readonly value: V }> {
      const box = this.#ledger.project(this.#prefix + key)?.cell ?? null;
      return box == null ? null : this.#unwrap(box);
    }

    #unwrap(box: Box): { readonly value: V } {
      const held = this.#codec.unwrap(box.value);
      if (held == null) throw new Error(`CellWrites: a ${box.value.lane} value in another lane`);
      return held;
    }
  }
}

const CELL: ReadonlySet<"cell"> = new Set(["cell"]);

function cleared(entry: CellWrites.EventEntry): CellWrites.Repaint {
  return entry.lane === "dated" ? { lane: "dated", date: entry.date, value: null } : { lane: "timepoint", value: null };
}
